import React, { useEffect, useState } from 'react';
import { FiMonitor, FiLayers, FiWifi, FiWifiOff, FiShield, FiAlertTriangle } from 'react-icons/fi';
import { getDevices, getGroups } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

const StatCard = ({ icon: Icon, label, value, color, sub }: any) => (
  <div className="bg-slate-800 rounded-2xl p-6 flex items-center gap-4">
    <div className={`${color} p-3 rounded-xl`}>
      <Icon className="text-white text-2xl" />
    </div>
    <div>
      <p className="text-slate-400 text-sm">{label}</p>
      <p className="text-white text-3xl font-bold">{value}</p>
      {sub && <p className="text-slate-500 text-xs mt-1">{sub}</p>}
    </div>
  </div>
);

const Dashboard = () => {
  const { t } = useLanguage();
  const [devices, setDevices] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [devRes, grpRes] = await Promise.all([getDevices(), getGroups()]);
        setDevices(devRes.data);
        setGroups(grpRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const onlineDevices = devices.filter(d => d.status === 'online').length;
  const offlineDevices = devices.filter(d => d.status !== 'online').length;
  const blockedDevices = devices.filter(d => d.is_blocked).length;
  const blockedOnline = devices.filter(d => d.is_blocked && d.status === 'online').length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-400">Loading...</div></div>;

  return (
    <div>
      <h1 className="text-white text-3xl font-bold mb-8">{t('dashboard')}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon={FiMonitor} label={t('totalDevices')} value={devices.length} color="bg-blue-600"
          sub={`${onlineDevices} online / ${offlineDevices} offline`} />
        <StatCard icon={FiWifi} label={t('online')} value={onlineDevices} color="bg-green-600"
          sub={`${Math.round(devices.length ? onlineDevices/devices.length*100 : 0)}% of total`} />
        <StatCard icon={FiShield} label={t('blocked')} value={blockedDevices} color="bg-red-600"
          sub={blockedOnline > 0 ? `⚠️ ${blockedOnline} blocked but online` : 'All blocked are offline'} />
        <StatCard icon={FiLayers} label={t('groups')} value={groups.length} color="bg-purple-600"
          sub={`${devices.filter(d => !d.group_id).length} ungrouped devices`} />
      </div>

      {blockedDevices > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-6 flex items-center gap-3">
          <FiAlertTriangle className="text-red-400 text-xl flex-shrink-0" />
          <p className="text-red-400 text-sm">
            <strong>{blockedDevices} device{blockedDevices > 1 ? 's' : ''} blocked.</strong> Check the Devices page for recovery keys.
          </p>
        </div>
      )}

      <div className="bg-slate-800 rounded-2xl p-6">
        <h2 className="text-white text-xl font-semibold mb-4">{t('recentDevices')}</h2>
        {devices.length === 0 ? (
          <div className="text-center py-12">
            <FiMonitor className="text-slate-600 text-5xl mx-auto mb-3" />
            <p className="text-slate-400">{t('noDevices')}</p>
            <p className="text-slate-500 text-sm mt-1">{t('noDevicesDesc')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {devices.slice(0, 8).map((device) => (
              <div key={device.id} className="flex items-center justify-between bg-slate-700 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  {device.status === 'online' ? <FiWifi className="text-green-400" /> : <FiWifiOff className="text-slate-500" />}
                  <div>
                    <p className="text-white font-medium">{device.hostname}</p>
                    <p className="text-slate-400 text-sm">
                      {device.os_type} • {device.group_name || t('noGroup')}
                      {device.serial_number && ` • SN: ${device.serial_number}`}
                      {device.agent_version && ` • v${device.agent_version}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {device.is_blocked && (
                    <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm">🔒 {t('blocked')}</span>
                  )}
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    device.status === 'online' ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-400'
                  }`}>
                    {device.status === 'online' ? t('online') : 'Offline'}
                  </span>
                  {device.last_blocked_at && (
                    <span className="text-slate-500 text-xs">
                      Blocked: {new Date(device.last_blocked_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
